//src/components/notification/NotificationUnit.tsx
import React, { useState } from "react";
import styles from "./NotificationUnit.module.css";
import unseenIcon from "../../assets/icons/unseenIcon.svg";
import confirmationFace from "../../assets/icons/request-confirmation.svg";
import orangeHornAlert from "../../assets/icons/orangeHornAlert.svg";
import redHornAlert from "../../assets/icons/redHornAlert.svg";
import { truncateContent } from "../../utils/ContentTruncationComputer";
import {
    Notification,
    SeenState,
    NotificationIconType,
} from "../../gql/types";
import { createdAgo } from "../../utils/TimeStringComputer";
import NotificationActionWorkflow from "./workflow/NotificationActionWorkflow";
import { useToastContext } from "contexts/ToastContext";
import { useLayout } from "components/layout/LayoutContext";
import { useUser } from "contexts/UserContext";


interface NotificationUnitProps {
    notification: Notification;
    onClick?: (notification: Notification) => void;
}

const NotificationUnit: React.FC<NotificationUnitProps> = ({
    notification,
    onClick,
}) => {
    const [showWorkflow, setShowWorkflow] = useState(false);
    const [isUnseenNotification, setIsUnseenNotification] = useState(notification.seenState === SeenState.Unseen);
    const { triggerToastEvent } = useToastContext();
    const { baseZIndex } = useLayout();
    const { user } = useUser();

    //1. Handle alert icon logic
    // TODO: Handle horn alerts properly with different emergency settings
    const getAlertIcon = (iconType: NotificationIconType): string => {
        switch (iconType) {
            case NotificationIconType.ActionRequired:
                return redHornAlert;
            case NotificationIconType.Info:
                return orangeHornAlert;
            case NotificationIconType.Reminder:
                return confirmationFace;
            default:
                // Log unrecognized icon types
                console.warn(`[UNIMPLEMENTED] Unrecognized icon type: ${iconType}`);
                return confirmationFace;
        }
    };

    //2. Handle notification text
    // TODO: Handle formattedText and rawText properly with ContentTruncationComputer.ts. Also display formatted text correctly. 
    const text = truncateContent(notification.content.formattedText || notification.content.rawText);

    //3. Handle notification click
    const handleClick = () => {
        setIsUnseenNotification(false);
        setShowWorkflow(true);
        onClick?.(notification);
    };

    const handleDataChange = (success: boolean) => {
        if (success) {
            triggerToastEvent();
        }
        setShowWorkflow(false);
    };

    return (
        <>
            <div
                className={styles.notificationUnit}
                onClick={handleClick}
            >
                <div className={styles.leftSection}>
                    <div className={styles.alertWrapper}>
                        <img
                            src={getAlertIcon(notification.icon)}
                            className={styles.mainIcon}
                            data-testid="main-icon"
                        />
                        {isUnseenNotification && (
                            <div className={styles.unseenIndicator}>
                                <img src={unseenIcon} className={styles.unseenIcon} data-testid="unseen-icon" />
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.rightSection}>
                    <div className={styles.contentWrapper}>
                        <span className={styles.content}>
                            {text}
                            <span className={styles.creationTime}> {createdAgo(notification.createdAt)}</span>
                        </span>
                    </div>
                </div>
            </div>
            {showWorkflow && (
                <NotificationActionWorkflow
                    notification={notification}
                    user={user}
                    onClose={() => setShowWorkflow(false)}
                    baseZIndex={baseZIndex + 1}
                    onDataChange={handleDataChange}
                />
            )}
        </>
    );
};

export default NotificationUnit;

